import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { RestrictedPermission } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { IS_SANDBOX, sandboxHomePath } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

type PermissionRestrictedRouteProps = Readonly<{
  children: ReactElement;
  requiredPermissions: RestrictedPermission[];
}>;

// The staff sandbox blocks navigation to HOME, and a blocked navigation never changes the URL — so
// falling back there would leave the visitor on a page they cannot see and cannot leave.
const fallbackRoute = () => (IS_SANDBOX ? sandboxHomePath() : APP_ROUTE.HOME);

export const PermissionRestrictedRoute = ({
  children,
  requiredPermissions,
}: PermissionRestrictedRouteProps) => {
  const { data: userPermissions, isError } = useFetchUserPermissionsQuery(undefined);

  if (isError) {
    return <Navigate to={fallbackRoute()} replace />;
  }

  if (!userPermissions) {
    return null;
  }

  const hasRequiredPermissions = requiredPermissions.every(
    (permission) => userPermissions[permission]?.allowed === true,
  );

  if (!hasRequiredPermissions) {
    return <Navigate to={fallbackRoute()} replace />;
  }

  return children;
};
